import type { FacetType } from '@seek/chalice-types';
import { useMelwaysInfo } from '@seek/melways-react';
import { useMemo } from 'react';

import { useSERPHasExternalJobs } from 'src/modules/hooks/useSERPHasExternalJobs';
import { includedKeyword } from 'src/modules/related-search-seo/keyword-filters';
import { useSelector } from 'src/store/react';
import type { RelatedSearch } from 'src/store/results/types';
import {
  selectFacets,
  selectRelatedSearches,
  selectSearchQuery,
} from 'src/store/selectors';

export type FilterList =
  | { filterListType: 'related'; filteredSearches: RelatedSearch[] }
  | { filterListType: 'facets'; filteredSearches: FacetType[] }
  | undefined;

const useSeoFooter = () => {
  const hasExternalJobs = useSERPHasExternalJobs();
  const relatedSearches = useSelector(selectRelatedSearches);
  const facets = useSelector(selectFacets);
  const searchQuery = useSelector(selectSearchQuery);
  const { language: currentLanguage } = useMelwaysInfo();

  const isLocationOnlySerp =
    searchQuery &&
    !searchQuery.keywords &&
    !searchQuery.classification &&
    searchQuery.where;

  const shouldRenderDistinctTitleFacets =
    currentLanguage === 'en' && isLocationOnlySerp;

  return useMemo(() => {
    const filteredSearches = relatedSearches
      ? relatedSearches.filter(({ keywords }) => includedKeyword(keywords))
      : [];

    const distinctTitleFacets =
      facets && facets.distinctTitle
        ? facets.distinctTitle.filter(
            ({ label }) => typeof label === 'string' && includedKeyword(label),
          )
        : [];

    let relatedList: FilterList;

    if (shouldRenderDistinctTitleFacets && distinctTitleFacets.length > 0) {
      relatedList = {
        filterListType: 'facets',
        filteredSearches: distinctTitleFacets,
      };
    } else if (filteredSearches.length > 0) {
      relatedList = { filterListType: 'related', filteredSearches };
    }

    const shouldRenderRelatedSearch =
      relatedList && relatedList?.filteredSearches.length > 0;

    return {
      tabIndex: -1,
      hasExternalJobs,
      relatedList,
      shouldRenderRelatedSearch,
    };
  }, [
    relatedSearches,
    facets,
    shouldRenderDistinctTitleFacets,
    hasExternalJobs,
  ]);
};

export default useSeoFooter;
